import {
  ArrowRightIcon,
  CaretDownIcon,
  CaretLeftIcon,
  HeadsetIcon,
  SparkleIcon,
} from "@phosphor-icons/react";
import { cn } from "@reactive-resume/utils";
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router";

import { useDialog } from "@/client/stores/dialog";

import { USER_SPACE_NAVIGATION, USER_SPACE_PATHS } from "../../config/navigation";
import type { NavigationItem, NavigationSection } from "../../types/navigation";

type PlanCode = "FREE" | "SMART_START" | "SMART_JOB" | "SMART_PRO";

type DesktopSidebarProps = {
  collapsed?: boolean;
  onCollapse?: () => void;
  currentPlanName?: string;
  currentPlanCode?: PlanCode;
};

type SidebarLinkProps = {
  item: NavigationItem;
  collapsed: boolean;
  nested?: boolean;
};

type SidebarSectionProps = {
  section: NavigationSection;
  collapsed: boolean;
  expanded: boolean;
  onToggle: () => void;
  onExpandSidebar?: () => void;
};

const PLAN_NAME_CLASS_NAMES: Record<PlanCode, string> = {
  FREE: "text-indigo-600",
  SMART_START: "text-emerald-600",
  SMART_JOB: "text-violet-600",
  SMART_PRO: "text-indigo-800",
};

const isPathActive = (pathname: string, path: string) => {
  if (path === USER_SPACE_PATHS.dashboard) {
    return pathname === path || pathname === `${path}/`;
  }

  return pathname === path || pathname.startsWith(`${path}/`);
};

const SidebarLink = ({ item, collapsed, nested = false }: SidebarLinkProps) => {
  const { pathname } = useLocation();

  const IconComponent = item.icon;

  const isActive = isPathActive(pathname, item.path);

  if (item.disabled) {
    return (
      <div
        aria-disabled="true"
        title={collapsed ? item.label : undefined}
        className={cn(
          "flex cursor-not-allowed items-center rounded-xl",
          "text-slate-400 opacity-60",
          nested ? "min-h-10" : "min-h-11",
          collapsed ? "justify-center px-2" : "gap-3 px-3",
        )}
      >
        {!nested && <IconComponent aria-hidden size={20} className="shrink-0" />}

        {!collapsed && (
          <span className={cn("truncate font-medium", nested ? "text-[13px]" : "text-sm")}>
            {item.label}
          </span>
        )}
      </div>
    );
  }

  return (
    <Link
      to={item.path}
      title={collapsed ? item.label : undefined}
      aria-current={isActive ? "page" : undefined}
      className={cn(
        "relative flex items-center rounded-xl",
        "transition-colors duration-200",
        "focus-visible:outline-none focus-visible:ring-2",
        "focus-visible:ring-indigo-500",
        nested ? "min-h-10" : "min-h-11",
        collapsed ? "justify-center px-2" : "gap-3 px-3",
        isActive
          ? "bg-indigo-50 text-indigo-700"
          : "text-slate-600 hover:bg-slate-50 hover:text-indigo-950",
      )}
    >
      {isActive && !collapsed && !nested && (
        <span
          aria-hidden
          className={cn("absolute inset-y-2 left-0", "w-0.5 rounded-r-full", "bg-emerald-400")}
        />
      )}

      {!nested && (
        <IconComponent
          aria-hidden
          size={20}
          weight={isActive ? "fill" : "regular"}
          className="shrink-0"
        />
      )}

      {!collapsed && (
        <span
          className={cn("min-w-0 flex-1 truncate font-medium", nested ? "text-[13px]" : "text-sm")}
        >
          {item.label}
        </span>
      )}
    </Link>
  );
};

const SidebarSection = ({
  section,
  collapsed,
  expanded,
  onToggle,
  onExpandSidebar,
}: SidebarSectionProps) => {
  const { pathname } = useLocation();

  const SectionIcon = section.icon;

  const containsActiveItem = section.items.some((item) => isPathActive(pathname, item.path));

  if (!section.label) {
    return (
      <div className="space-y-1">
        {section.items.map((item) => (
          <SidebarLink key={item.path} item={item} collapsed={collapsed} />
        ))}
      </div>
    );
  }

  const handleSectionClick = () => {
    if (collapsed) {
      onExpandSidebar?.();
      return;
    }

    onToggle();
  };

  return (
    <section>
      <button
        type="button"
        onClick={handleSectionClick}
        aria-expanded={!collapsed && expanded}
        title={collapsed ? section.label : undefined}
        className={cn(
          "flex min-h-11 w-full items-center rounded-xl",
          "transition-colors duration-200",
          "focus-visible:outline-none focus-visible:ring-2",
          "focus-visible:ring-indigo-500",
          collapsed ? "justify-center px-2" : "gap-3 px-3",
          containsActiveItem ? "text-indigo-700" : "text-indigo-950 hover:bg-slate-50",
        )}
      >
        {SectionIcon && (
          <SectionIcon
            aria-hidden
            size={21}
            weight={containsActiveItem ? "duotone" : "regular"}
            className="shrink-0"
          />
        )}

        {!collapsed && (
          <>
            <span className="min-w-0 flex-1 truncate text-left text-sm font-semibold">
              {section.label}
            </span>

            <CaretDownIcon
              aria-hidden
              size={15}
              className={cn("shrink-0 transition-transform duration-300", expanded && "rotate-180")}
            />
          </>
        )}
      </button>

      {!collapsed && (
        <div
          className={cn(
            "grid transition-[grid-template-rows,opacity]",
            "duration-300 ease-out",
            expanded ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
          )}
        >
          <div className="overflow-hidden">
            <div className={cn("relative ml-6 mt-1 space-y-0.5", "border-l border-slate-200 pl-3")}>
              {section.items.map((item) => {
                const isActive = isPathActive(pathname, item.path);

                return (
                  <span key={item.path} className="relative block">
                    <span
                      aria-hidden
                      className={cn(
                        "absolute -left-[15px] top-1/2 size-1.5",
                        "-translate-y-1/2 rounded-full",
                        isActive ? "bg-indigo-600" : "bg-indigo-950",
                      )}
                    />

                    <SidebarLink item={item} collapsed={false} nested />
                  </span>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export const DesktopSidebar = ({
  collapsed = false,
  onCollapse,
  currentPlanName = "FREE",
  currentPlanCode = "FREE",
}: DesktopSidebarProps) => {
  const { open: openOffers } = useDialog("forfait");

  const handleOpenOffers = () => {
    openOffers("create", {
      id: "forfait",
    });
  };

  const { pathname } = useLocation();

  const hasActivePlan = currentPlanCode !== "FREE";

  const displayedPlanName = hasActivePlan ? currentPlanName : "FREE";

  const planNameClassName = PLAN_NAME_CLASS_NAMES[currentPlanCode];

  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(
      USER_SPACE_NAVIGATION.filter((section) => section.label).map((section) => [
        section.label as string,
        section.defaultExpanded ?? false,
      ]),
    ),
  );

  useEffect(() => {
    const activeSection = USER_SPACE_NAVIGATION.find(
      (section) => section.label && section.items.some((item) => isPathActive(pathname, item.path)),
    );

    if (!activeSection?.label) {
      return;
    }

    setExpandedSections(
      Object.fromEntries(
        USER_SPACE_NAVIGATION.filter((section) => section.label).map((section) => [
          section.label as string,
          section.label === activeSection.label,
        ]),
      ),
    );
  }, [pathname]);

  const toggleSection = (label: string) => {
    setExpandedSections((currentSections) => {
      const sectionWillOpen = !currentSections[label];

      return Object.fromEntries(
        USER_SPACE_NAVIGATION.filter((section) => section.label).map((section) => [
          section.label as string,
          section.label === label ? sectionWillOpen : false,
        ]),
      );
    });
  };

  return (
    <aside
      className={cn(
        "sticky top-0 z-[100] hidden h-dvh shrink-0",
        "flex-col overflow-visible border-r border-slate-200",
        "bg-white px-3",
        "transition-[width] duration-300 lg:flex",
        collapsed ? "w-[76px]" : "w-[280px]",
      )}
    >
      {onCollapse && (
        <button
          type="button"
          onClick={onCollapse}
          aria-label={collapsed ? "Agrandir la barre latérale" : "Réduire la barre latérale"}
          title={collapsed ? "Agrandir la barre latérale" : "Réduire la barre latérale"}
          className={cn(
            "absolute -right-[18px] top-7 z-[110]",
            "grid size-9 place-items-center rounded-xl",
            "border border-slate-200 bg-white",
            "text-indigo-950 shadow-md",
            "transition-all duration-200",
            "hover:border-indigo-300 hover:bg-indigo-50",
            "hover:shadow-lg",
            "focus-visible:outline-none focus-visible:ring-2",
            "focus-visible:ring-indigo-500",
          )}
        >
          <CaretLeftIcon
            aria-hidden
            size={17}
            weight="bold"
            className={cn("transition-transform duration-300", collapsed && "rotate-180")}
          />
        </button>
      )}

      <div className={cn("flex h-24 shrink-0 items-center", collapsed ? "justify-center" : "px-3")}>
        <Link
          to={USER_SPACE_PATHS.dashboard}
          aria-label="Accéder au tableau de bord monCVpro"
          className={cn(
            "rounded-xl focus-visible:outline-none",
            "focus-visible:ring-2 focus-visible:ring-indigo-500",
          )}
        >
          <img
            src="/logo/mon-cv-pro_logo.svg"
            alt="monCVpro"
            className={cn(
              "w-auto object-contain",
              collapsed ? "h-11 max-w-12" : "h-[72px] max-w-40",
            )}
          />
        </Link>
      </div>

      <div
        className={cn(
          "min-h-0 flex-1 overflow-y-auto overflow-x-hidden",
          "scroll-smooth pb-4 pr-1",
          "[scrollbar-width:thin]",
          "[scrollbar-color:#6366f1_#ecfdf5]",
          "[&::-webkit-scrollbar]:w-1.5",
          "[&::-webkit-scrollbar-track]:rounded-full",
          "[&::-webkit-scrollbar-track]:bg-emerald-50",
          "[&::-webkit-scrollbar-thumb]:rounded-full",
          "[&::-webkit-scrollbar-thumb]:bg-indigo-500",
          "[&::-webkit-scrollbar-thumb]:transition-colors",
          "[&::-webkit-scrollbar-thumb:hover]:bg-indigo-700",
        )}
      >
        <nav aria-label="Navigation de l’espace utilisateur" className="space-y-2 py-2">
          {USER_SPACE_NAVIGATION.map((section, sectionIndex) => {
            const sectionKey = section.label ?? `section-${sectionIndex}`;

            return (
              <SidebarSection
                key={sectionKey}
                section={section}
                collapsed={collapsed}
                expanded={section.label ? Boolean(expandedSections[section.label]) : true}
                onToggle={() => {
                  if (section.label) {
                    toggleSection(section.label);
                  }
                }}
                onExpandSidebar={onCollapse}
              />
            );
          })}
        </nav>

        <div className="mt-6 space-y-4">
          <Link
            to={USER_SPACE_PATHS.help}
            title={collapsed ? "Centre d’aide" : undefined}
            className={cn(
              "flex min-h-12 items-center rounded-xl",
              "border border-indigo-600 bg-white",
              "text-[13px] font-semibold text-indigo-700",
              "transition-all duration-200",
              "hover:bg-indigo-50",
              "hover:shadow-sm",
              "focus-visible:outline-none focus-visible:ring-2",
              "focus-visible:ring-indigo-500",
              collapsed ? "justify-center px-2" : "justify-center gap-2 px-3",
            )}
          >
            <HeadsetIcon aria-hidden size={20} weight="duotone" />

            {!collapsed && <span>Centre d’aide</span>}
          </Link>

          {!collapsed && (
            <article
              className={cn(
                "rounded-2xl border border-slate-200",
                "bg-gradient-to-br from-white via-white to-indigo-50/70",
                "p-3.5",
                "shadow-[0_8px_24px_rgba(15,23,42,0.04)]",
              )}
            >
              <div className="flex min-w-0 items-center">
                <p className="min-w-0 truncate text-xs text-indigo-950">
                  Offre actuelle :{" "}
                  <strong className={cn("font-bold", planNameClassName)}>
                    {displayedPlanName}
                  </strong>
                </p>

                {hasActivePlan && (
                  <span
                    aria-label="Offre active"
                    className={cn(
                      "ml-auto size-2.5 shrink-0 rounded-full",
                      "bg-emerald-400",
                      "shadow-[0_0_0_4px_rgba(52,211,153,0.14)]",
                    )}
                  />
                )}
              </div>

              <button
                type="button"
                onClick={handleOpenOffers}
                className={cn(
                  "mt-4 flex min-h-12 items-center justify-between gap-2",
                  "rounded-xl bg-gradient-to-r",
                  "from-indigo-700 via-violet-700 to-indigo-600",
                  "px-4 text-[13px] font-semibold text-white",
                  "shadow-[0_8px_18px_rgba(79,70,229,0.2)]",
                  "transition-all duration-200",
                  "hover:-translate-y-0.5",
                  "hover:shadow-[0_12px_24px_rgba(79,70,229,0.26)]",
                  "focus-visible:outline-none focus-visible:ring-2",
                  "focus-visible:ring-indigo-500 focus-visible:ring-offset-2",
                )}
              >
                <span className="flex min-w-0 items-center gap-2">
                  <SparkleIcon aria-hidden size={17} weight="duotone" className="shrink-0" />

                  <span className="truncate">Découvrir les offres</span>
                </span>

                <ArrowRightIcon aria-hidden size={17} weight="bold" className="shrink-0" />
              </button>
            </article>
          )}
        </div>
      </div>
    </aside>
  );
};
